import { ArdoqId, GroupType, GroupingRule } from '@ardoq/api-types';
import { createReferenceIdFromParentAndChildId } from './parentChildUtils';
import {
  areSamePaths,
  clearBaseGroup,
  getAllPaths,
  getGroupRuleLabel,
  removeParent,
  repeat,
  setParentsAndAddGroupIfNeeded,
  toName,
  toNamedPair,
  toPathObject,
  updateParent,
} from './buildGroupsUtils';
import { groupBy, last, partition, uniq, uniqWith, zip } from 'lodash';
import {
  ComponentOrGroupId,
  GroupByArgs,
  GroupMap,
  GroupedComponents,
  GroupingResult,
  ParentGroupMap,
  PathObject,
  ViewError,
} from '../../types';
import { isTruthy } from '@ardoq/common-helpers';
import { isUseNewGrouping } from './isUseNewGrouping';
import { PathList } from './PathList';
import { AggregatedReferenceType } from '@ardoq/data-model';
import type { GraphInterface } from '../../graphInterface/graphInterface';
import { type I18nLocale, t } from '@ardoq/i18n';

// Examples sequential grouping by reference type
// https://docs.google.com/document/d/198QqIGoeJS139m46xbWNkuvgTGUUwSVpga4UJUjoWpE/edit#heading=h.8zyjg775q1tk

export const groupByReferenceTypeName = (args: GroupByArgs) => {
  const { targetId: referenceTypeName } = args.groupBy;
  const referencesToProcess = new Set(
    args.referenceIds.filter(
      referenceId =>
        args.graphInterface.getReferenceTypeNameByReferenceId(referenceId) ===
        referenceTypeName
    )
  );
  return groupReferences({ ...args, referencesToProcess });
};

export const groupByReferenceType = (args: GroupByArgs) => {
  const { targetId: referenceTypeId } = args.groupBy;
  const referencesToProcess = new Set(
    args.referenceIds.filter(
      referenceId =>
        args.graphInterface.getGlobalReferenceTypeId(referenceId) ===
        referenceTypeId
    )
  );
  return groupReferences({ ...args, referencesToProcess });
};

const groupReferences = ({
  groupBy,
  componentIds,
  graph,
  result,
  index,
  useNewGrouping,
  graphInterface,
  referencesToProcess,
  i18nLocale,
}: GroupByArgs & { referencesToProcess: Set<ArdoqId> }) => {
  const { direction } = groupBy;
  const linkedComponentsMap =
    direction === 'incoming' ? graph.sourceMap : graph.targetMap;

  const { paths, error } = getAllPaths(
    componentIds,
    linkedComponentsMap,
    referencesToProcess,
    i18nLocale
  );

  if (error) {
    result.errors.push({
      message: error,
      rule: groupBy,
      index,
    });
    return;
  }

  const buildParentGroupsError = buildParentGroups({
    parentLists: paths,
    groupBy,
    type: GroupType.REFERENCE,
    index,
    result,
    useNewGrouping,
    referenceTypes: graph.referenceTypes,
    graphInterface,
    i18nLocale,
  });

  if (!buildParentGroupsError) {
    Array.from(referencesToProcess).forEach(referenceId =>
      result.processedReferences.add(referenceId)
    );
  }
};

type BuildParentGroupsArgs = {
  parentLists: ArdoqId[][];
  groupBy: GroupingRule;
  type: GroupType;
  index: number;
  result: GroupingResult;
  useNewGrouping: boolean;
  referenceTypes: AggregatedReferenceType[];
  graphInterface: GraphInterface;
  i18nLocale: I18nLocale;
};
export const buildParentGroups = ({
  parentLists,
  groupBy,
  type,
  index,
  result,
  useNewGrouping,
  referenceTypes,
  graphInterface,
  i18nLocale,
}: BuildParentGroupsArgs) => {
  const error = validatedParentLists(
    graphInterface,
    parentLists,
    result.groupedComponents,
    result.parentGroupMap,
    result.groupMapRaw,
    groupBy,
    index,
    useNewGrouping,
    i18nLocale
  );

  if (error) {
    result.errors.push(error);
    return error;
  }

  const tails = new Set(result.pathList.map(({ tail }) => tail));
  const heads = new Set(
    result.pathList.flatMap(({ heads }) => Array.from(heads))
  );
  const refTypeHeads = new Set(
    result.pathList.map(({ refTypeHead }) => refTypeHead)
  );
  const subLabel = getGroupRuleLabel(groupBy, referenceTypes);
  const { groupMapRaw, parentGroupMap } = result;

  const { processed, notProcessed, existingProcessed } = getGroupingStrategies(
    isUseNewGrouping(useNewGrouping),
    groupBy.type
  ).reduce<HandleStrategyArguments>((acc, handler) => handler(acc), {
    processed: [],
    reprocess: [],
    notProcessed: parentLists,
    existingProcessed: new Set(),
    pathList: result.pathList,
    noMatch: [],
    type,
    subLabel,
    groupMapRaw,
    parentGroupMap,
    tails,
    heads,
    refTypeHeads,
    useNewGrouping,
    graphInterface,
  });

  const getCircularPaths = processed.filter(pathObject =>
    isCircularPath(graphInterface, pathObject)
  );

  if (getCircularPaths.length > 0) {
    const details = [
      t(
        i18nLocale,
        'graph:groupingErrors.circularPathsHeader',
        'The following paths have components trying to group themselves:'
      ),
      ...getCircularPaths.map(({ path }) =>
        path
          .map(componentId => toName(graphInterface, componentId))
          .join(' -> ')
      ),
    ];

    const error = {
      message: t(
        i18nLocale,
        'graph:groupingErrors.circularDependency',
        'Groups cannot create a circular dependency'
      ),
      rule: groupBy,
      index,
      details,
    };
    result.errors.push(error);
    return error;
  }

  const newGroups = processNewGroups(
    graphInterface,
    notProcessed,
    type,
    subLabel,
    result
  );
  const pathObjects = [
    ...result.pathList.filter(pathObject => !existingProcessed.has(pathObject)),
    ...processed,
    ...newGroups,
  ];
  result.pathList = new PathList();
  result.pathList.addPaths(pathObjects);

  addGroupedComponentsAndProcessedReferences(parentLists, result);

  return null;
};

const getGroupingStrategies = (
  useNewGrouping: boolean,
  groupByType: GroupType
) => {
  if (!useNewGrouping) {
    return [
      handleExtendHead,
      handleExtendTail,
      handleMerge,
      handleInsertBeforeTail,
    ];
  }

  if (groupByType === GroupType.REFERENCE) {
    return [
      handleExtendHeadGroupingResearch,
      handleExtendTailGroupingResearch,
      handleHeadsAndTailsDuplicate,
      handleHeadsAndHeadsDuplicate,
      handleTailsDuplicate,
    ];
  }
  return [handleExtendHead, handleInsertBeforeTail];
};

const isCircularPath = (
  graphInterface: GraphInterface,
  { path }: PathObject
) => {
  const componentPaths = path.filter(graphInterface.isComponent);
  return new Set(componentPaths).size < componentPaths.length;
};

type ProcessorArgs = {
  processedGroups: Record<string, string[][]>;
  exitingGroupsProcessed: Record<string, PathObject[]>;
  type: GroupType;
  subLabel: string;
  groupMapRaw: GroupMap;
  parentGroupMap: ParentGroupMap;
  graphInterface: GraphInterface;
  useNewGrouping?: boolean;
};

type GenericHandlerConfig = {
  matcher: (args: {
    heads: Set<string>;
    tails: Set<string>;
    refTypeHeads: Set<string>;
    listHead: string;
    listTail: string;
  }) => boolean;
  grouper: (list: string[]) => string;
  isCheckHeads: boolean;
  processor: (args: ProcessorArgs) => PathObject[];
  isReprocess: boolean;
};

type HandleStrategyArguments = {
  notProcessed: string[][];
  reprocess: string[][];
  processed: PathObject[];
  noMatch: PathObject[];
  existingProcessed: Set<PathObject>;
  pathList: PathObject[];
  type: GroupType;
  subLabel: string;
  groupMapRaw: GroupMap;
  parentGroupMap: ParentGroupMap;
  tails: Set<string>;
  heads: Set<string>;
  refTypeHeads: Set<string>;
  useNewGrouping: boolean;
  graphInterface: GraphInterface;
};

const genericHandler =
  ({ matcher, grouper, isCheckHeads, processor }: GenericHandlerConfig) =>
  (args: HandleStrategyArguments): HandleStrategyArguments => {
    if (args.notProcessed.length === 0) {
      return args;
    }

    const {
      type,
      subLabel,
      pathList,
      existingProcessed,
      groupMapRaw,
      parentGroupMap,
      tails,
      heads,
      refTypeHeads,
      useNewGrouping,
      graphInterface,
    } = args;
    const processedNewPathes: Set<string> = new Set();
    const { processed, notProcessed } = args.notProcessed.reduce<{
      processed: string[][];
      notProcessed: string[][];
    }>(
      ({ processed, notProcessed }, list) => {
        const listHead = list[0];
        const listTail = last(list)!;
        if (matcher({ heads, tails, refTypeHeads, listHead, listTail })) {
          processed.push(list);
          removeExistingGroups(list, tails, processedNewPathes);
        } else {
          notProcessed.push(list);
        }
        return { processed, notProcessed };
      },
      {
        processed: [],
        notProcessed: [],
      }
    );

    if (processed.length === 0) {
      return args;
    }

    const processedGroups = groupBy(processed, grouper);
    const keysProcessed = new Set(Object.keys(processedGroups));
    const { processed: exitingGroupsProcessed, noMatch } = pathList.reduce<{
      processed: Record<string, PathObject[]>;
      noMatch: PathObject[];
    }>(
      ({ processed, noMatch }, pathObject) => {
        if (isCheckHeads && hasIntersection(keysProcessed, pathObject.heads)) {
          keysProcessed.forEach(id => {
            if (pathObject.heads.has(id)) {
              processed[pathObject.path.find(pathId => pathId === id)!].push(
                pathObject
              );
              existingProcessed.add(pathObject);
            }
          });
        } else if (!isCheckHeads && keysProcessed.has(pathObject.tail)) {
          processed[pathObject.tail].push(pathObject);
          existingProcessed.add(pathObject);
        } else if (processedNewPathes.has(pathObject.tail)) {
          existingProcessed.add(pathObject);
        } else {
          noMatch.push(pathObject);
        }
        return { processed, noMatch };
      },
      {
        processed: Object.fromEntries(
          Array.from(keysProcessed).map(key => [key, []])
        ),
        noMatch: [],
      }
    );

    const newPathObjects = processor({
      processedGroups,
      exitingGroupsProcessed,
      type,
      subLabel,
      groupMapRaw,
      parentGroupMap,
      useNewGrouping,
      graphInterface,
    });

    return {
      ...args,
      processed: [
        ...args.processed.filter(({ tail }) => !processedNewPathes.has(tail)),
        ...newPathObjects,
      ],
      notProcessed,
      pathList: [...newPathObjects, ...noMatch],
    };
  };

/*
  grouping research

  - the more restrictive grouping has the effect that some new paths are not
    combined with existing groups
  - these need to be moved to the correct base groups
    (called duplicate handler)
  - duplicated groups need to be merged properly, e.g. only the labels are
    relevant e.g. have to be merged properly
    (handled in getContainingPathObjectsAndMergeSublabels)
  - question is if parent grouping really requires a new implementation
    (e.g insert-before-tail and extend head are very similar)
*/

const genericHandlerGroupingResearch =
  ({
    matcher,
    grouper,
    isCheckHeads,
    processor,
    isReprocess,
  }: GenericHandlerConfig) =>
  (args: HandleStrategyArguments): HandleStrategyArguments => {
    if (args.notProcessed.length + args.reprocess.length === 0) {
      return args;
    }

    const {
      type,
      subLabel,
      pathList,
      existingProcessed,
      groupMapRaw,
      parentGroupMap,
      tails,
      heads,
      refTypeHeads,
      useNewGrouping,
      graphInterface,
    } = args;

    const [processedFirstTime, notProcessed] = partition(
      args.notProcessed,
      list =>
        matcher({
          heads,
          tails,
          refTypeHeads,
          listHead: list[0],
          listTail: last(list)!,
        })
    );
    const [reprocessed] = partition(args.reprocess, list =>
      matcher({
        heads,
        tails,
        refTypeHeads,
        listHead: list[0],
        listTail: last(list)!,
      })
    );

    const consolidateTargets: Map<
      string[],
      { heads: PathObject[]; tails: PathObject[] }
    > = new Map();

    if (reprocessed.length > 0) {
      // Reprocessing is only done for handleExtendTailGroupingResearch
      reprocessed.forEach(path => {
        const heads = args.processed.filter(pathObject =>
          pathStartsWith(pathObject, path)
        );
        if (heads.length > 0) {
          if (!consolidateTargets.has(path)) {
            consolidateTargets.set(path, { heads: [], tails: [] });
          }
          consolidateTargets.get(path)!.heads.push(...heads);
        }
      });
    }

    const processed = uniqWith(
      [...processedFirstTime, ...reprocessed],
      areSamePaths
    );

    if (processed.length === 0) {
      return args;
    }

    const processedGroups = groupBy(processed, grouper);
    const keysProcessed = new Set(Object.keys(processedGroups));
    const exitingGroupsProcessed = pathList.reduce<
      Record<string, PathObject[]>
    >(
      (processed, pathObject) => {
        if (isCheckHeads && hasIntersection(keysProcessed, pathObject.heads)) {
          keysProcessed.forEach(id => {
            if (pathObject.heads.has(id)) {
              processed[pathObject.path.find(pathId => pathId === id)!].push(
                pathObject
              );
            }
          });
        } else if (!isCheckHeads && keysProcessed.has(pathObject.tail)) {
          processed[pathObject.tail].push(pathObject);
        }
        return processed;
      },
      Object.fromEntries(Array.from(keysProcessed).map(key => [key, []]))
    );

    const newPathObjects = uniqWith(
      processor({
        processedGroups,
        exitingGroupsProcessed,
        type,
        subLabel,
        groupMapRaw,
        parentGroupMap,
        useNewGrouping,
        graphInterface,
      }),
      ({ path: pathA }, { path: pathB }) => areSamePaths(pathA, pathB)
    ).filter(
      pathObject =>
        getContainingPathObjectsAndMergeSublabels(
          graphInterface,
          [...args.processed, ...pathList],
          pathObject
        ).length === 0
    );

    Array.from(consolidateTargets.keys()).forEach(path => {
      const tails = newPathObjects.filter(pathObject =>
        pathEndsWith(pathObject, path)
      );
      consolidateTargets.get(path)!.tails.push(...tails);
    });

    const [consolidatedPathObjects, processedPathObjects] = consolidate(
      graphInterface,
      consolidateTargets,
      parentGroupMap
    );
    const consolidatedNewPathObjects = [
      ...newPathObjects.filter(
        pathObject => !processedPathObjects.includes(pathObject)
      ),
      ...consolidatedPathObjects,
    ];

    const [noMatch, existingProcessedList] = partition(
      pathList,
      pathObject =>
        getContainingPathObjectsAndMergeSublabels(
          graphInterface,
          consolidatedNewPathObjects,
          pathObject
        ).length === 0
    );

    existingProcessedList.forEach(pathObject =>
      existingProcessed.add(pathObject)
    );

    return {
      ...args,
      processed: [
        ...args.processed.filter(
          pathObject =>
            getContainingPathObjectsAndMergeSublabels(
              graphInterface,
              consolidatedNewPathObjects,
              pathObject
            ).length === 0
        ),
        ...consolidatedNewPathObjects,
      ],
      notProcessed,
      reprocess:
        isReprocess && isUseNewGrouping(useNewGrouping)
          ? args.notProcessed
          : [],
      pathList: [...consolidatedNewPathObjects, ...noMatch],
    };
  };

const processExtendHeads = ({
  processedGroups: processGroupsExtendHead,
  exitingGroupsProcessed: exitingGroupsExtendHead,
  type,
  subLabel,
  groupMapRaw,
  parentGroupMap,
  useNewGrouping = false,
  graphInterface,
}: ProcessorArgs) =>
  Object.entries(processGroupsExtendHead).reduce<PathObject[]>(
    (newPaths, [key, paths]) => {
      exitingGroupsExtendHead[key].forEach(
        ({ path: existingPath, sublabels: existingSublabels, refTypeHead }) => {
          const refTypeHeadIndex = existingPath.indexOf(refTypeHead);
          const baseGroup = existingPath[refTypeHeadIndex - 1];
          const index = existingPath.indexOf(key);
          const existingAncestorGroups = existingPath.slice(0, index);
          const descendantGroups = existingPath.slice(index + 1);
          const currentParentGroupId = last(existingAncestorGroups) || null;
          paths.forEach(path => {
            if (isUseNewGrouping(useNewGrouping)) {
              clearBaseGroup(baseGroup, path, parentGroupMap);
            }
            removeParent(key, last(existingAncestorGroups)!, parentGroupMap);
            setParentsAndAddGroupIfNeeded(
              graphInterface,
              path,
              currentParentGroupId,
              type,
              subLabel,
              groupMapRaw,
              parentGroupMap
            );
            newPaths.push(
              toPathObject(
                graphInterface,
                [...existingAncestorGroups, ...path, ...descendantGroups],
                [
                  ...existingSublabels.slice(0, index),
                  ...repeat([subLabel], path.length - 1),
                  ...existingSublabels.slice(index),
                ]
              )
            );
          });
        }
      );
      return newPaths;
    },
    []
  );

const processMerge = ({
  processedGroups: processGroupsMerge,
  exitingGroupsProcessed: exitingGroupsMerge,
  type,
  subLabel,
  groupMapRaw,
  parentGroupMap,
  graphInterface,
}: ProcessorArgs) => {
  const processedAncestorGroups = new Set<string>();
  return Object.entries(processGroupsMerge).reduce<PathObject[]>(
    (newPaths, [key, paths]) => {
      exitingGroupsMerge[key].forEach(existingPathObject => {
        const { path: existingPath, sublabels: existingSublabels } =
          existingPathObject;
        const index = existingPath.indexOf(key);
        const existingAncestorGroups = existingPath.slice(0, index);
        const currentParentGroupId = last(existingAncestorGroups) || null;
        const group = groupMapRaw.get(existingPath[index]);
        if (!group!.subLabels) {
          group!.subLabels = [];
        }
        if (!group!.subLabels.includes(subLabel)) {
          group!.subLabels.push(subLabel);
        }
        newPaths.push(existingPathObject);
        const ancestorGroupsKey = [...existingAncestorGroups, key].join('-');
        if (!processedAncestorGroups.has(ancestorGroupsKey)) {
          processedAncestorGroups.add(ancestorGroupsKey);
          paths.forEach(path => {
            setParentsAndAddGroupIfNeeded(
              graphInterface,
              path,
              currentParentGroupId,
              type,
              subLabel,
              groupMapRaw,
              parentGroupMap
            );
            newPaths.push(
              toPathObject(
                graphInterface,
                [...existingAncestorGroups, ...path],
                [
                  ...existingSublabels.slice(0, index),
                  ...repeat([subLabel], path.length - 1),
                  [],
                ]
              )
            );
          });
        }
      });
      return newPaths;
    },
    []
  );
};

const processExtendTails = ({
  processedGroups: processGroupsExtendTail,
  exitingGroupsProcessed: exitingGroupsExtendTail,
  type,
  subLabel,
  groupMapRaw,
  parentGroupMap,
  useNewGrouping = false,
  graphInterface,
}: ProcessorArgs) =>
  Object.entries(processGroupsExtendTail).reduce<PathObject[]>(
    (newPaths, [key, paths]) => {
      exitingGroupsExtendTail[key].forEach(
        ({ path: existingPath, sublabels: existingSublabels, refTypeHead }) => {
          const refTypeHeadIndex = existingPath.indexOf(refTypeHead);
          const baseGroup = existingPath[refTypeHeadIndex - 1];
          const currentParentGroupId =
            existingPath[existingPath.length - 2] || null;
          paths.forEach(path => {
            if (isUseNewGrouping(useNewGrouping)) {
              clearBaseGroup(baseGroup, path, parentGroupMap);
            }
            setParentsAndAddGroupIfNeeded(
              graphInterface,
              path,
              currentParentGroupId,
              type,
              subLabel,
              groupMapRaw,
              parentGroupMap
            );
            newPaths.push(
              toPathObject(
                graphInterface,
                [...existingPath.slice(0, -1), ...path],
                [
                  ...existingSublabels.slice(0, -1),
                  ...repeat([subLabel], path.length - 1),
                  [],
                ]
              )
            );
          });
        }
      );
      return newPaths;
    },
    []
  );

const processInsertBeforeTail = ({
  processedGroups: processGroupsInsertBeforeTail,
  exitingGroupsProcessed: exitingGroupsInsertBeforeTail,
  type,
  subLabel,
  groupMapRaw,
  parentGroupMap,
  graphInterface,
}: ProcessorArgs) =>
  Object.entries(processGroupsInsertBeforeTail).reduce<PathObject[]>(
    (newPaths, [key, paths]) => {
      exitingGroupsInsertBeforeTail[key].forEach(
        ({ path: existingPath, sublabels: existingSublabels }) => {
          const currentParentGroupId =
            existingPath[existingPath.length - 2] || null;
          paths.forEach(path => {
            setParentsAndAddGroupIfNeeded(
              graphInterface,
              path,
              currentParentGroupId,
              type,
              subLabel,
              groupMapRaw,
              parentGroupMap
            );
            newPaths.push(
              toPathObject(
                graphInterface,
                [...existingPath.slice(0, -1), ...path],
                [
                  ...existingSublabels.slice(0, -1),
                  ...repeat([subLabel], path.length - 1),
                  [],
                ]
              )
            );
          });
        }
      );
      return newPaths;
    },
    []
  );

const processDuplicate = ({
  processedGroups,
  exitingGroupsProcessed,
  type,
  subLabel,
  groupMapRaw,
  parentGroupMap,
  graphInterface,
}: ProcessorArgs) =>
  Object.entries(processedGroups).reduce<PathObject[]>(
    (newPaths, [key, paths]) => {
      exitingGroupsProcessed[key].forEach(existingPathObject => {
        const { path: existingPath, sublabels: existingSublabels } =
          existingPathObject;
        const index = existingPath.indexOf(key);
        const refTypeGroupHead = getRefTypeGroupHeadIndex(
          graphInterface,
          index,
          existingPath
        );
        const existingAncestorGroups = existingPath.slice(0, refTypeGroupHead);
        const currentParentGroupId = last(existingAncestorGroups) || null;
        paths.forEach(path => {
          newPaths.push(existingPathObject);
          setParentsAndAddGroupIfNeeded(
            graphInterface,
            path,
            currentParentGroupId,
            type,
            subLabel,
            groupMapRaw,
            parentGroupMap
          );
          newPaths.push(
            toPathObject(
              graphInterface,
              [...existingAncestorGroups, ...path],
              [
                ...existingSublabels.slice(0, refTypeGroupHead),
                ...repeat([subLabel], path.length - 1),
                [],
              ]
            )
          );
        });
      });
      return newPaths;
    },
    []
  );

const processNewGroups = (
  graphInterface: GraphInterface,
  paths: string[][],
  type: GroupType,
  subLabel: string,
  result: GroupingResult
) =>
  paths.map(path => {
    const currentParentGroupId = null;
    setParentsAndAddGroupIfNeeded(
      graphInterface,
      path,
      currentParentGroupId,
      type,
      subLabel,
      result.groupMapRaw,
      result.parentGroupMap
    );
    return toPathObject(graphInterface, path, [
      ...repeat([subLabel], path.length - 1),
      [],
    ]);
  });

const handleExtendHead = genericHandler({
  matcher: ({ heads, listTail }) => heads.has(listTail!),
  grouper: (list: string[]) => last(list)!,
  isCheckHeads: true,
  processor: processExtendHeads,
  isReprocess: false,
});

const handleMerge = genericHandler({
  matcher: ({ heads, listHead }) => heads.has(listHead),
  grouper: (list: string[]) => list[0]!,
  isCheckHeads: true,
  processor: processMerge,
  isReprocess: false,
});

const handleExtendTail = genericHandler({
  matcher: ({ tails, listHead }) => tails.has(listHead),
  grouper: (list: string[]) => list[0]!,
  isCheckHeads: false,
  processor: processExtendTails,
  isReprocess: false,
});

const handleInsertBeforeTail = genericHandler({
  matcher: ({ tails, listTail }) => tails.has(listTail),
  grouper: (list: string[]) => last(list)!,
  isCheckHeads: false,
  processor: processInsertBeforeTail,
  isReprocess: false,
});

const handleExtendHeadGroupingResearch = genericHandlerGroupingResearch({
  matcher: ({ refTypeHeads, listTail }) => refTypeHeads.has(listTail!),
  grouper: (list: string[]) => last(list)!,
  isCheckHeads: true,
  processor: processExtendHeads,
  isReprocess: true,
});

const handleExtendTailGroupingResearch = genericHandlerGroupingResearch({
  matcher: ({ tails, listHead }) => tails.has(listHead),
  grouper: (list: string[]) => list[0]!,
  isCheckHeads: false,
  processor: processExtendTails,
  isReprocess: false,
});

const handleHeadsAndTailsDuplicate = genericHandlerGroupingResearch({
  matcher: ({ heads, listTail }) => heads.has(listTail),
  grouper: (list: string[]) => last(list)!,
  isCheckHeads: true,
  processor: processDuplicate,
  isReprocess: false,
});

const handleHeadsAndHeadsDuplicate = genericHandlerGroupingResearch({
  matcher: ({ heads, listHead }) => heads.has(listHead),
  grouper: (list: string[]) => list[0]!,
  isCheckHeads: true,
  processor: processDuplicate,
  isReprocess: false,
});

const handleTailsDuplicate = genericHandlerGroupingResearch({
  matcher: ({ tails, listTail }) => tails.has(listTail),
  grouper: (list: string[]) => last(list)!,
  isCheckHeads: false,
  processor: processDuplicate,
  isReprocess: false,
});

const removeExistingGroups = (
  path: string[],
  tails: Set<string>,
  processedTails: Set<string>
) =>
  path.forEach(id => {
    if (tails.has(id)) {
      processedTails.add(id);
    }
  });

const getContainingPathObjectsAndMergeSublabels = (
  graphInterface: GraphInterface,
  existingPathObjects: PathObject[],
  pathObject: PathObject
) => {
  const containingPaths = getContainingPaths(
    graphInterface,
    existingPathObjects,
    pathObject
  );

  containingPaths.forEach(existingPathObject => {
    existingPathObject.sublabels = mergeSublabels(
      graphInterface,
      existingPathObject,
      pathObject
    );
  });

  return containingPaths;
};

const getContainingPaths = (
  graphInterface: GraphInterface,
  pathObjects: PathObject[],
  { path }: PathObject
) => {
  // All components are in the same base groups, otherwise the grouping
  // would not be performed.
  const refTypeGroupHeadIndex = getRefTypeGroupHeadIndex(
    graphInterface,
    path.length - 1,
    path
  );
  const refTypeGroup = path.slice(refTypeGroupHeadIndex);

  return pathObjects.filter(({ path: existingPath }) => {
    const startIndex = existingPath.indexOf(refTypeGroup[0]);
    return refTypeGroup.every(
      (id, index) => id === existingPath[startIndex + index]
    );
  });
};

const mergeSublabels = (
  graphInterface: GraphInterface,
  pathObjectExisting: PathObject,
  pathObject: PathObject
): string[][] => {
  const refTypeGroupHeadIndex = getRefTypeGroupHeadIndex(
    graphInterface,
    pathObject.path.length - 1,
    pathObject.path
  );
  const startIndex = pathObjectExisting.path.indexOf(
    pathObject.path[refTypeGroupHeadIndex]
  );

  if (
    pathObject.sublabels
      .slice(refTypeGroupHeadIndex)
      .every((labels, index) =>
        labels.every(label =>
          pathObjectExisting.sublabels[startIndex + index].includes(label)
        )
      )
  ) {
    return pathObjectExisting.sublabels;
  }

  const length = pathObject.path.length - refTypeGroupHeadIndex;
  return [
    ...pathObjectExisting.sublabels.slice(0, startIndex),
    ...zip(
      pathObjectExisting.sublabels.slice(startIndex, startIndex + length),
      pathObject.sublabels.slice(
        refTypeGroupHeadIndex,
        refTypeGroupHeadIndex + length
      )
    ).map(([sublabelsA, sublabelsB]) =>
      uniq([...(sublabelsA || []), ...(sublabelsB || [])]).filter(isTruthy)
    ),
    ...pathObjectExisting.sublabels.slice(startIndex + length),
  ];
};

const getRefTypeGroupHeadIndex = (
  graphInterface: GraphInterface,
  index: number,
  existingPath: string[]
) => {
  let refTypeGroupHeadIndex = index;
  while (
    refTypeGroupHeadIndex > 0 &&
    graphInterface.isComponent(existingPath[refTypeGroupHeadIndex - 1])
  ) {
    refTypeGroupHeadIndex--;
  }
  return refTypeGroupHeadIndex;
};

export const getBaseGroup = (
  graphInterface: GraphInterface,
  index: number,
  existingPath: string[]
) => {
  if (index < 1) {
    return null;
  }

  const refTypeGroupHeadIndex = getRefTypeGroupHeadIndex(
    graphInterface,
    index,
    existingPath
  );
  return refTypeGroupHeadIndex > 0
    ? existingPath[refTypeGroupHeadIndex - 1]
    : null;
};

export const getGroupIfIsComponentGroup = (
  graphInterface: GraphInterface,
  id = ''
) => (graphInterface.isComponent(id) ? id : null);

const pathStartsWith = (
  { refTypeHead, path: existingPath }: PathObject,
  path: string[]
) => {
  const refTypeHeadIndex = existingPath.indexOf(refTypeHead);
  return path.every(
    (id, index) => id === existingPath[index + refTypeHeadIndex]
  );
};

const pathEndsWith = ({ path: existingPath }: PathObject, path: string[]) => {
  const startIndex = existingPath.length - path.length;
  return path.every((id, index) => id === existingPath[index + startIndex]);
};

const hasIntersection = <T>(setA: Set<T>, setB: Set<T>) =>
  setA.size === 0 || setB.size === 0
    ? false
    : new Set([...setA, ...setB]).size < setA.size + setB.size;

type ConsolidateTargets = Map<
  string[],
  { heads: PathObject[]; tails: PathObject[] }
>;

const consolidate = (
  graphInterface: GraphInterface,
  consolidateTargets: ConsolidateTargets,
  parentGroupMap: ParentGroupMap
) =>
  Array.from(consolidateTargets).reduce<[PathObject[], PathObject[]]>(
    ([newPathObjects, processed], [path, { heads, tails }]) => [
      [
        ...newPathObjects,
        ...heads.reduce<PathObject[]>(
          (
            newPathObjects,
            { path: pathHead, refTypeHead, sublabels: sublabelsHead }
          ) => {
            const refTypeHeadIndex = pathHead.indexOf(refTypeHead);
            return [
              ...newPathObjects,
              ...tails.reduce<PathObject[]>(
                (
                  newPathObjects,
                  { path: pathTail, sublabels: sublabelsTail }
                ) => {
                  removeParent(
                    refTypeHead,
                    pathHead[refTypeHeadIndex - 1],
                    parentGroupMap
                  );
                  updateParent(
                    pathHead[refTypeHeadIndex + path.length],
                    last(pathTail)!,
                    parentGroupMap,
                    pathHead[refTypeHeadIndex + path.length - 1]
                  );
                  const newPath = [
                    ...pathTail,
                    ...pathHead.slice(refTypeHeadIndex + path.length),
                  ];
                  const newSublabels = [
                    ...sublabelsTail.slice(0, -1),
                    ...sublabelsHead.slice(refTypeHeadIndex + path.length - 1),
                  ];
                  return [
                    ...newPathObjects,
                    toPathObject(graphInterface, newPath, newSublabels),
                  ];
                },
                []
              ),
            ];
          },
          []
        ),
      ],
      [...processed, ...heads, ...tails],
    ],
    [[], []]
  );

export const validatedParentLists = (
  graphInterface: GraphInterface,
  parentLists: ArdoqId[][],
  groupedComponents: GroupedComponents,
  parentGroupMap: ParentGroupMap,
  groupMapRaw: GroupMap,
  groupBy: GroupingRule,
  index: number,
  useNewGrouping: boolean,
  i18nLocale: I18nLocale
) =>
  isUseNewGrouping(useNewGrouping)
    ? validatedParentListsBasedOnNonComponentGroups(
        graphInterface,
        parentLists,
        groupedComponents,
        parentGroupMap,
        groupMapRaw,
        groupBy,
        index,
        i18nLocale
      )
    : validatedParentLists_(
        graphInterface,
        parentLists,
        groupedComponents,
        parentGroupMap,
        groupMapRaw,
        groupBy,
        index,
        i18nLocale
      );

const validatedParentLists_ = (
  graphInterface: GraphInterface,
  parentLists: ArdoqId[][],
  groupedComponents: GroupedComponents,
  parentGroupMap: ParentGroupMap,
  groupMapRaw: GroupMap,
  groupBy: GroupingRule,
  index: number,
  i18nLocale: I18nLocale
) =>
  parentLists.reduce<ViewError | null>((error, ancestorChain) => {
    if (error) {
      return error;
    }
    // Parent means in this context the source end of a a pair of connected
    // components.
    // The current parent is either the parent of the head or the tail of the
    // list or null.
    // The parent list is valid if the parent of any of the nodes in the list
    // is either the current parent or null.
    const currentParents =
      parentGroupMap.get(last(ancestorChain)!) ||
      parentGroupMap.get(ancestorChain[0]);

    if (
      ancestorChain.some(
        ancestorId =>
          groupedComponents.has(ancestorId) &&
          !isSameSet(parentGroupMap.get(ancestorId), currentParents)
      )
    ) {
      const componentId =
        currentParents === parentGroupMap.get(last(ancestorChain)!)
          ? last(ancestorChain)
          : ancestorChain[0];
      const details = [
        t(
          i18nLocale,
          'graph:groupingErrors.groupToBeExtended',
          'The group to be extended'
        ),
        toNamedPair(
          graphInterface,
          parentGroupMap,
          groupMapRaw,
          componentId!,
          i18nLocale
        ),
        t(
          i18nLocale,
          'graph:groupingErrors.componentsAndCurrentGroups',
          'Components to be grouped and their current groups'
        ),
        ...ancestorChain.map(ancestorId =>
          toNamedPair(
            graphInterface,
            parentGroupMap,
            groupMapRaw,
            ancestorId,
            i18nLocale
          )
        ),
      ];

      return {
        message: t(
          i18nLocale,
          'graph:groupingErrors.groupExtensionMembership',
          'An existing component group can only be extended if all new components already belong to the group or to no group'
        ),
        rule: groupBy,
        index,
        details,
      };
    }

    return error;
  }, null);

const validatedParentListsBasedOnNonComponentGroups = (
  graphInterface: GraphInterface,
  parentLists: ArdoqId[][],
  groupedComponents: GroupedComponents,
  parentGroupMap: ParentGroupMap,
  groupMapRaw: GroupMap,
  groupBy: GroupingRule,
  index: number,
  i18nLocale: I18nLocale
) => {
  const toNonComponentAncestors = getToNonComponentAncestors(
    graphInterface,
    parentGroupMap
  );
  return parentLists.reduce<ViewError | null>((error, ancestorChain) => {
    if (error) {
      return error;
    }
    // Parent means in this context the source end of a a pair of connected
    // components.
    // The current parent is the non-component-type group (e.g. workspace group)
    // of either the head or the tail of the
    // list or null.
    // The parent list is valid if the parent of any of the nodes in the list
    // is either the current parent or null.
    const currentParents = toNonComponentAncestors(
      parentGroupMap.get(last(ancestorChain)!) ||
        parentGroupMap.get(ancestorChain[0])
    );

    if (
      ancestorChain.some(
        ancestorId =>
          groupedComponents.has(ancestorId) &&
          !isSameSet(
            toNonComponentAncestors(parentGroupMap.get(ancestorId)),
            currentParents
          )
      )
    ) {
      const componentId =
        currentParents === parentGroupMap.get(last(ancestorChain)!)
          ? last(ancestorChain)
          : ancestorChain[0];
      const details = [
        t(
          i18nLocale,
          'graph:groupingErrors.groupToBeExtended',
          'The group to be extended'
        ),
        toNamedPair(
          graphInterface,
          parentGroupMap,
          groupMapRaw,
          componentId!,
          i18nLocale,
          toNonComponentAncestors
        ),
        t(
          i18nLocale,
          'graph:groupingErrors.componentsAndCurrentGroups',
          'Components to be grouped and their current groups'
        ),
        ...ancestorChain.map(ancestorId =>
          toNamedPair(
            graphInterface,
            parentGroupMap,
            groupMapRaw,
            ancestorId,
            i18nLocale,
            toNonComponentAncestors
          )
        ),
      ];

      return {
        message: t(
          i18nLocale,
          'graph:groupingErrors.groupExtensionMembershipBaseGroup',
          'An existing component group can only be extended if all new components already belong to the same base group or to no group'
        ),
        rule: groupBy,
        index,
        details,
      };
    }

    return error;
  }, null);
};
const getToNonComponentAncestors = (
  graphInterface: GraphInterface,
  parentGroupMap: ParentGroupMap
) => {
  const toNonComponentAncestors = (
    parents?: Set<ComponentOrGroupId>
  ): Set<ComponentOrGroupId> => {
    if (!parents) {
      return new Set<string>();
    }
    const [componentTypeParents, nonComponentTypeParents] = partition(
      Array.from(parents),
      graphInterface.isComponent
    );
    return new Set([
      ...nonComponentTypeParents,
      ...componentTypeParents.flatMap(id =>
        Array.from(toNonComponentAncestors(parentGroupMap.get(id)))
      ),
    ]);
  };
  return toNonComponentAncestors;
};

const isSameSet = <T>(
  setA: Set<T> | undefined | null,
  setB: Set<T> | undefined | null
) => {
  if (!setA && !setB) {
    return true;
  }
  if (!setA || !setB) {
    return false;
  }
  return (
    setA.size === setB.size && setA.size === new Set([...setA, ...setB]).size
  );
};

export const addGroupedComponentsAndProcessedReferences = (
  parentLists: string[][],
  result: GroupingResult
) => {
  parentLists
    .flatMap(path => path.slice(1))
    .forEach(componentId => result.groupedComponents.add(componentId));

  if (result.isParentRelationAsReference) {
    // Add parent-child references to processed references.
    const { processedReferences } = result;
    parentLists.forEach(parentList => {
      // To see how this works
      // [grandparent, parent, child, grandchild]
      // two lists, one slice(0, -1) the other slice (1)
      // [grandparent, parent, child]
      // [parent, child, grandchild]
      // and ziping these two gives parent-child pairs
      // [[grandparent, parent], [parent, child], [child, grandchild]]
      zip(parentList.slice(0, -1), parentList.slice(1)).forEach(
        ([parentId, childId]) => {
          const id = createReferenceIdFromParentAndChildId(parentId!, childId!);
          processedReferences.add(id);
        }
      );
    });
  }
};
